// src/pages/AuthCallbackPage.jsx
// Handles OAuth callback - receives tokens from URL and stores them
// Also handles pending user redirects

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleCallback } = useAuth();
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  useEffect(() => {
    async function processCallback() {
      const params = new URLSearchParams(location.search);
      
      // Check for pending user redirect
      if (params.get('pending') === 'true') {
        setPending(true);
        setPendingEmail(params.get('email') || '');
        return;
      }
      
      // Check for disabled account
      if (params.get('error') === 'disabled') {
        setError(params.get('message') || 'Your account has been disabled');
        return;
      }
      
      // Check for error from OAuth provider
      const oauthError = params.get('error');
      if (oauthError) {
        setError(params.get('error_description') || oauthError);
        return;
      }

      // Get tokens from URL
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      const expiresIn = params.get('expires_in');

      if (!accessToken) {
        setError('No access token received');
        return;
      }

      try {
        const success = await handleCallback({
          access_token: accessToken,
          refresh_token: refreshToken,
          expires_in: expiresIn
        });

        if (success) {
          // Get the original destination or default to admin
          const redirectTo = localStorage.getItem('auth_redirect') || '/admin';
          localStorage.removeItem('auth_redirect');
          navigate(redirectTo, { replace: true });
        } else {
          setError('Failed to process authentication');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Authentication failed');
      }
    }

    processCallback();
  }, [location.search, handleCallback, navigate]);

  // Show pending approval screen
  if (pending) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.pendingIcon}>⏳</div>
          <h2 style={styles.title}>Pending Approval</h2>
          <p style={styles.pendingMessage}>
            Your sign-in{pendingEmail ? ` with ${pendingEmail}` : ''} has been recorded. 
            The administrator has been notified. You will receive an email once you are approved for access.
          </p>
          <button 
            onClick={() => navigate('/login', { replace: true })}
            style={styles.button}
          >
            ← Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.title}>Authentication Failed</h2>
          <p style={styles.error}>{error}</p>
          <button 
            onClick={() => navigate('/login')}
            style={styles.button}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.spinner} />
        <h2 style={styles.title}>Signing you in...</h2>
        <p style={styles.subtitle}>Please wait while we complete authentication</p>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0D1117',
    padding: 20
  },
  card: {
    background: 'linear-gradient(135deg, #1a2332 0%, #11181C 100%)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 16,
    padding: '48px 40px',
    maxWidth: 420,
    width: '100%',
    textAlign: 'center'
  },
  spinner: {
    width: 48,
    height: 48,
    border: '3px solid rgba(62,207,142,0.2)',
    borderTop: '3px solid #3ECF8E',
    borderRadius: '50%',
    margin: '0 auto 24px',
    animation: 'spin 1s linear infinite'
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: '#ffffff',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: 14,
    color: '#9ca3af',
    margin: 0
  },
  pendingIcon: {
    fontSize: 48,
    marginBottom: 16
  },
  pendingMessage: {
    fontSize: 14,
    color: '#9ca3af',
    margin: '0 0 24px 0',
    lineHeight: 1.6
  },
  errorIcon: {
    fontSize: 48,
    marginBottom: 16
  },
  error: {
    fontSize: 14,
    color: '#ef4444',
    margin: '0 0 24px 0'
  },
  button: {
    background: '#3ECF8E',
    color: '#11181C',
    border: 'none',
    padding: '12px 24px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer'
  }
};

// Add keyframes for spinner (you'll need this in your CSS or add via styled-components)
// @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
